import URLX from './Url';
import { Action } from 'redux';
import * as itemCache from './CurrentPage/cache';
import * as appShell from './AppShell/utils';
import { autoResolveDynamicData } from './DynamicData';
import { clearCacheAndUnregisterServiceWorker } from './service-worker/registration';
import { isIE } from './device-type';
import { nukeCache } from './service-worker/cache';

export const LINK_IDENTIFIER_HEADER = 'X-Link-Identifier';
export const REQUEST_ACTION_HEADER = 'X-Request-Action';
export const INCLUDE_APP_SHELL_DATA_HEADER = 'X-IncludeAppShellData';
export const RESOLVE_DYNAMIC_DATA_HEADER = 'X-ResolveDynamicData';
export const VERSION_HEADER_NAME = 'X-Server-Version';
export const SETTINGS_VERSION_HEADER_NAME = 'X-Settings-Version';
export const MARKET_HEADER = 'X-Market';

export const ADD_TO_CART_ACTION = 'addtocart';
export const CLICK_ACTION = 'click';

const siteSettingsVersionKey = "site-settings-version";

import localDb from './local-db';
const settingsVersionStorage = localDb('settingsVersion');

export type LoadFailureAction = Action & {
  error: any;
  url: URLX;
};

type LoadFailure = {
  status: number;
  response: Response;
};

export type OnAuthRequiredCallback = (errResponse: Response) => Promise<unknown>;
let onAuthRequired: OnAuthRequiredCallback | null = null;

/**
 * Use this to implement "seamless auth", meaning that the passed in callback here
 * will be called if the server returns 401. This gives you a chance to login the user
 * and once the promise resolves that the callback returns the original request
 * will be replayed.
 */
export function setOnAuthRequired(callback: OnAuthRequiredCallback) {
  onAuthRequired = callback;
}

export function get(url: string | URLX, headers?: { [name: string]: string }) {
  return load(url, 'get', undefined, headers);
}

export function getItem<T>(url: string | URLX, useCache: boolean = false) {
  url = typeof url === 'string' ? new URLX(url) : url;
  let cachePromise: Promise<T> | null = null;
  if (useCache) {
    const cacheItem = itemCache.getCacheItem(url);
    if (cacheItem) {
      cachePromise = Promise.resolve((cacheItem as any) as T);
    }
  }

  // Even if we use the cache we still want to get a fresh version of it
  // and update the cache
  const fetchPromise = (getJson(url) as Promise<T>).then((item: T) => {
    const cacheItem = item as {};

    if (itemCache.isCacheItem(cacheItem)) {
      itemCache.setCacheItem(cacheItem);
    }

    return item;
  });

  return cachePromise ? cachePromise : fetchPromise;
}

export function getItems<T>(url: string | URLX) {
  return getJson(url).then((items: any[]) => {
    if (items) {
      items.forEach(i => {
        if (itemCache.isCacheItem(i)) {
          itemCache.setCacheItem(i);
        }
      });
    }

    return items as T[];
  });
}

export async function getJson(url: string | URLX, headers?: { [name: string]: string }, includeDefaultHeaders = true) {
  const result = await load(url, 'get', undefined, headers, includeDefaultHeaders).then(r => r.json());
  autoResolveDynamicData(result);
  return result;
}

export function post(
  url: string | URLX,
  body?: any,
  headers?: { [name: string]: string },
  includeDefaultHeaders = true,
) {
  return load(url, 'post', body, headers, includeDefaultHeaders);
}

export async function postJson(
  url: string | URLX,
  body?: any,
  headers?: { [name: string]: string },
  includeDefaultHeaders = true,
) {
  const result = await load(url, 'post', body, headers, includeDefaultHeaders).then(r => r.json());
  autoResolveDynamicData(result);
  return result;
}

type HttpMethod = 'get' | 'post';

function load(
  url: string | URLX,
  method: HttpMethod,
  body?: any,
  headers: { [name: string]: string } = {},
  includeDefaultHeaders = true,
): Promise<Response> {
  // We append the extra querystring here because we can't request the same url for Ajax as what's in
  // the browser address bar. If we use the same url, the browser will use the JSON response from the browser
  // cache to serve up to the user if the user goes back from the site and then forward to it.
  url = new URLX(url);

  if (includeDefaultHeaders) {
    // Oddly enough these extra headers causes issues when trying to preload json responses.
    // Any extra headers will invalidate
    headers = Object.assign(
      {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
        'X-Client-Version': CURRENT_VERSION,
      },
      headers,
    );
  }

  let promise = fetch(url.hiddenHref, {
    headers,
    credentials: 'same-origin',
    method,
    body: body !== undefined ? JSON.stringify(body) : isIE() ? null : undefined, // We use `null` instead of `undefined` here because otherwise IE will send `"undefined"` as body
  })
    .then<any>(r => {
      return validateServerVersions(r).then(result => {
        if(result) {
          console.info('Nuking the local caches because the server has a higher major version/higher version or settings version');
          return Promise.all([
            clearCacheAndUnregisterServiceWorker(),
            itemCache.nukeCache(),
            appShell.nukeAppShell(),
            nukeCache(),
          ]).then(() => {
            // Might seem drastic to do a full page reload when we get an Ajax response since it
            // will probably interrupt the user. But a major version change means that data structures
            // differ in a way that the client has to update. Major version changes should be few and far
            // between enough to not make this a usability issue.
            window.location.reload();
            // Return never resolving promise to pause the chain so the page can reload
            return new Promise(() => null);
          });
        } else {
          return r;
        }
      })
    })
    .then(r => {
      if (r.status >= 200 && r.status < 400) {
        return r;
      } else {
        if (r.status === 401 && onAuthRequired) {
          return onAuthRequired(r).then(
            () => {
              return load(url, method, body, headers, includeDefaultHeaders);
            },
            () => {
              return Promise.reject({
                status: r.status,
                response: r,
              } as LoadFailure);
            },
          );
        }

        return Promise.reject({
          status: r.status,
          response: r,
        } as LoadFailure);
      }
    });

  if (process.env.NODE_ENV !== 'production') {
    const delayNumber =
      (window.chaosMonkeyMode && window.chaosMonkeyMode.delay) || Math.round(Math.random() * 2 * 100) / 100;
    const failNumber = (window.chaosMonkeyMode && window.chaosMonkeyMode.fail) || 0.5;

    const delay = (value: any) => {
      console.debug('Delaying request for', delayNumber, 'seconds', (url as URLX).hiddenHref);
      return new Promise<any>(resolve => setTimeout(() => resolve(value), delayNumber * 1000));
    };

    const randomlyFail = (value: any) => {
      if (Math.random() < failNumber) {
        console.debug('Simulating failed request, probability: ', failNumber, (url as URLX).hiddenHref);
        return Promise.reject({
          status: 500,
        } as LoadFailure);
      } else {
        return value;
      }
    };

    if (window.chaosMonkeyMode) {
      promise = promise.then(delay).then(randomlyFail);
    }
  }

  return promise;
}

function validateServerVersions(response: Response) {
  return serverHasDifferentSettingsVersion(response).then( x => {
    if(x) {
      console.info('Server has a higher settings version');
      return true;
    }

    const isNewMajorVersion = serverHasDifferentMajorVersion(response);
    if(isNewMajorVersion) {
      console.info('Server has a higher major version');
      return true;
    }

    const isNewVersionAndShouldBeTreatedAsMajor = TREAT_EVERY_RELEASE_AS_MAJOR && serverHasDifferentVersion(response);
    if(isNewVersionAndShouldBeTreatedAsMajor) {
      console.info('Server has a higher version and treatEveryReleaseAsMajor is set to true in config');
      return true;
    }

    return false;
  })
}

function serverHasDifferentVersion(response: Response) {
  const serverVersion = response.headers.get(VERSION_HEADER_NAME);
  return serverVersion ? serverVersion !== CURRENT_VERSION : false;
}

function serverHasDifferentMajorVersion(response: Response) {
  // If the response is an error we might have failed to add the server version
  if (!response.headers.has(VERSION_HEADER_NAME)) {
    if (response.ok || response.status === 503) {
      // If the response is missing the version header but not an error
      // the response has been intercepted by a maintenance page or something
      // like it which indicates that we should clear everything and reload.
      return true;
    }
    console.warn("Response didn't contain a server version in header");
    return false;
  }

  const serverVersion = response.headers.get(VERSION_HEADER_NAME);
  return serverVersion ? majorVersionDiffers(serverVersion, CURRENT_VERSION) : false;
}

function serverHasDifferentSettingsVersion(response: Response) {
  return settingsVersionStorage.getItem<string>(siteSettingsVersionKey).then(storedVersion => {
    // If the response is an error we might have failed to add the server version
    if (!response.headers.has(SETTINGS_VERSION_HEADER_NAME)) {
      console.warn("Response didn't contain a settings version in header");
      settingsVersionStorage.removeItem(siteSettingsVersionKey);
      return false;
    }

    const headerVersion = response.headers.get(SETTINGS_VERSION_HEADER_NAME);
    if(headerVersion === null) {
      console.warn("Response didn't contain a settings version value in header");
      settingsVersionStorage.removeItem(siteSettingsVersionKey);
      return false;
    }
    
    if(!RELOAD_FOR_EMPTY_CLIENT_SETTINGS_VERSION && (storedVersion === null || storedVersion === "")) {
      storedVersion = headerVersion.toString();
      settingsVersionStorage.setItem<string>(siteSettingsVersionKey, storedVersion);
    }

    if(storedVersion !== headerVersion) {
      storedVersion = headerVersion;
      settingsVersionStorage.setItem<string>(siteSettingsVersionKey, storedVersion);
      return true;
    }
    return false;
  })
}

let isContinuousDeployEnvironment = false;
export function setIsContinuousDeployEnvironment(value: boolean) {
  isContinuousDeployEnvironment = value;
}

export function majorVersionDiffers(version1: string, version2: string) {
  const version1Array = (version1 || '').split('.');
  const version2Array = (version2 || '').split('.');

  // For continuous deployment purposes, if you deploy the same version but with
  // differing revision number (e.g. 1.0.0.1234 vs 1.0.0.1235) we consider this
  // to be a major version to prevent projects in early phases have to increase major
  // version all the time.
  if (
    isContinuousDeployEnvironment &&
    version1Array.length === version2Array.length &&
    version1Array.length === 4 &&
    `${version1Array[0]}.${version1Array[1]}.${version1Array[2]}` ===
      `${version2Array[0]}.${version2Array[1]}.${version2Array[2]}` &&
    version1Array[3] !== version2Array[3]
  ) {
    return true;
  }

  return version1Array[0] !== version2Array[0];
}
